import {useMemo} from 'react'
import {InlineRow} from '@/components/shared/InlineRow'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, XCircle, AlertTriangle, Clock, X} from 'lucide-react'
import {useSearchParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {
    AutoAssignRuleControllerAllExecutionsEndpoint,
    AutoAssignRuleControllerRuleExecutionsEndpoint,
} from '@/types/endpoint-contracts'
import {AutoAssignRuleExecutionStatus, AutoAssignRuleExecutionFilterParams} from '@/types/auto-assign-rule'
import {formatTimestamp, formatDuration} from '@/lib/date-utils'
import {BadgeVariant} from '@/components/ui/badge'
import {PageLayout} from '@/components/shared/PageLayout'
import {TextLink} from '@/components/shared/TextLink'
import {IconText} from '@/components/shared/IconText'
import {StatusText} from '@/components/shared/StatusText'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'

/**
 * Auto-Assign Rule Executions list page — Phase C.2 unified search migration
 * (issue 342.07). Closes issue 269 by replacing the hardcoded
 * `<SelectItem>` block (Success / Partial / Failed) with the multi-select
 * status chip rendered by {@link UnifiedSearchBar}. The chip values come
 * from the {@link AutoAssignRuleControllerAllExecutionsEndpoint} contract,
 * whose STATUS filter declares `typeTable: 'AutoAssignRuleExecutionStatus'`
 * — the AppConfig delivers the localized labels and codes server-side, so
 * adding a new status value (or renaming one) flows automatically without
 * touching this component or the i18n files.
 *
 * Spec invariants this component upholds:
 *   • Zero implicit default filters — empty URL shows every execution log.
 *   • Status filter values are sourced from FilterDescriptor.enumValues
 *     (AppConfig.typeValuesByName), not hardcoded i18n keys (REG-269).
 *   • Rule-scope sentinel — the `ruleId` URL param is preserved across
 *     page-size / sort changes; the legacy "× clear rule" badge stays.
 */
export function AutoAssignRuleExecutions() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.autoAssignRules.executionsPageTitle)
    const [searchParams, setSearchParams] = useSearchParams()
    const ruleIdParam = searchParams.get(AutoAssignRuleExecutionFilterParams.ruleId)

    // Pick the endpoint contract matching the current view. Both endpoints
    // declare an identical CSV-of-ENUM `status` filter (typeTable points at
    // AutoAssignRuleExecutionStatus on both), so the UnifiedSearchBar chip
    // shape stays consistent regardless of rule-scope. (De-verb override in
    // the Java controllers gives these distinct TS export names — spec §4.5.)
    const endpoint = ruleIdParam
        ? AutoAssignRuleControllerRuleExecutionsEndpoint
        : AutoAssignRuleControllerAllExecutionsEndpoint

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint,
        sortable: true,
        defaultSortBy: AutoAssignRuleControllerAllExecutionsEndpoint.sort.EXECUTED_AT,
        defaultSortDir: SortDir.DESC,
    })

    // Fetch rule if filtering by rule
    const {data: rule} = useQuery({
        queryKey: ['auto-assign-rule', ruleIdParam],
        queryFn: () => services.autoAssignRules.detail(ruleIdParam!),
        enabled: !!ruleIdParam,
    })

    // Fetch executions
    const {data: executions, isLoading, error} = useQuery({
        queryKey: ['auto-assign-rule-executions', ruleIdParam, queryString, page, pageSize],
        queryFn: () => ruleIdParam
            ? services.autoAssignRules.executions(ruleIdParam, Object.fromEntries(new URLSearchParams(queryString)))
            : services.autoAssignRules.allExecutions(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const getStatusIcon = (status: AutoAssignRuleExecutionStatus) => {
        switch (status) {
            case AutoAssignRuleExecutionStatus.SUCCESS:
                return <Icon icon={CheckCircle} className="text-status-success"/>
            case AutoAssignRuleExecutionStatus.PARTIAL:
                return <Icon icon={AlertTriangle} className="text-status-warning"/>
            case AutoAssignRuleExecutionStatus.FAILED:
                return <Icon icon={XCircle} className="text-status-danger"/>
        }
    }

    const getStatusBadgeVariant = (status: AutoAssignRuleExecutionStatus) => {
        switch (status) {
            case AutoAssignRuleExecutionStatus.SUCCESS:
                return BadgeVariant.SUCCESS
            case AutoAssignRuleExecutionStatus.PARTIAL:
                return BadgeVariant.WARNING
            case AutoAssignRuleExecutionStatus.FAILED:
                return BadgeVariant.DESTRUCTIVE
        }
    }

    const clearRuleFilter = () => {
        searchParams.delete(AutoAssignRuleExecutionFilterParams.ruleId)
        setSearchParams(searchParams)
    }

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
            || !!ruleIdParam
    }, [searchAndFilters, ruleIdParam])

    const filters = (
        <>
            <UnifiedSearchBar
                contract={endpoint}
                value={searchAndFilters}
                onChange={setSearchAndFilters}
            />
            {ruleIdParam && rule && (
                <Badge variant={BadgeVariant.SECONDARY} className="items-center gap-1">
                    {i18n.autoAssignRules.rule}: {rule.name}
                    <Icon
                        icon={X}
                        size="xs"
                        onClick={clearRuleFilter}
                        data-testid="btn-clear-rule-filter"
                        aria-label={i18n.common.clear}
                    />
                </Badge>
            )}
        </>
    )

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.jobExecution}
            title={i18n.autoAssignRules.ruleExecutionHistory as string}
            count={executions?.totalElements}
            description={hasActiveFilters ? i18n.common.matchingFilters : undefined}
            filters={filters}
            loading={isLoading}
            error={error}
            data-testid="auto-assign-rule-executions"
            emptyState={
                <InlineEmptyText>{i18n.autoAssignRules.noExecutions}</InlineEmptyText>
            }
            pagination={executions ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={executions.totalElements}
                    totalPages={executions.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-rule">{i18n.autoAssignRules.rule}</TableHead>
                        <TableHead data-testid="col-executed-at">{i18n.autoAssignRules.executedAt}</TableHead>
                        <TableHead data-testid="col-duration">{i18n.autoAssignRules.duration}</TableHead>
                        <TableHead className="text-right" data-testid="col-matched">{i18n.autoAssignRules.matched}</TableHead>
                        <TableHead className="text-right" data-testid="col-added">{i18n.autoAssignRules.added}</TableHead>
                        <TableHead className="text-right" data-testid="col-removed">{i18n.autoAssignRules.removed}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {executions?.content.map((exec) => (
                        <TableRow key={exec.id}>
                            <TableCell>
                                <InlineRow gap="md">
                                    {getStatusIcon(exec.status)}
                                    <Badge variant={getStatusBadgeVariant(exec.status)}>
                                        {exec.status}
                                    </Badge>
                                </InlineRow>
                            </TableCell>
                            <TableCell>
                                <TextLink
                                    testId={`link-rule-detail-${exec.ruleId}`}
                                    to={routes.accessGovernance.rules.detail(exec.ruleId)}
                                    variant="bold"
                                >
                                    {exec.ruleName}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <IconText icon={Clock} size="sm" className="text-muted-foreground">
                                    {formatTimestamp(exec.executedAt)}
                                </IconText>
                            </TableCell>
                            <TableCell>
                                {formatDuration(exec.durationMs)}
                            </TableCell>
                            <TableCell className="text-right">
                                {exec.identitiesMatched}
                            </TableCell>
                            <TableCell className="text-right">
                                <StatusText tone="success">+{exec.assignmentsAdded}</StatusText>
                            </TableCell>
                            <TableCell className="text-right">
                                <StatusText tone="danger">-{exec.removalsScheduled}</StatusText>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
